'use client'

import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Client, ClientTypeEnum, IdTypeEnum, LegalFormEnum } from '../data/schema'
import { useTranslation } from 'react-i18next'
import { ClientsTableParams } from '..'
import { add_client, check_name_unique, update_client } from '@/api/client'
import { PhoneInput } from '@/views/clients/components/phone-number-input'
import { debounceAsync } from '@/utils/debounce'
import { useEffect } from 'react'
import { Separator } from '@/components/ui/separator'
import { DatePicker } from '@/components/date-picker'

interface Props {
  currentRow?: Client
  open: boolean
  onOpenChange: (open: boolean) => void
  params: ClientsTableParams
  setParams: React.Dispatch<React.SetStateAction<ClientsTableParams>>
  onUpdateSuccess?: (updatedClient: Client) => void;
}

export function ClientsActionDialog({ currentRow, open, onOpenChange, params, setParams, onUpdateSuccess }: Props) {
  const { t } = useTranslation();
  const isEdit = !!currentRow
  const check_username = debounceAsync(check_name_unique, 200)

  // Define comprehensive schema for validation
  const formSchema = z.object({
    // Type
    client_type: ClientTypeEnum,

    // Core
    name: z.string().min(4, { message: t('client_name_required') }),
    email: z.string().email({ message: t('email_invalid') }).optional().or(z.literal('')),
    phone: z.string().optional().nullable(),
    address: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),

    // ID (Unified field)
    idnumber: z.string().optional().nullable(),

    // Individual
    id_type: IdTypeEnum.optional().nullable(),
    birth_date: z.string().optional(),
    nationality: z.string().optional(),
    profession: z.string().optional(),

    // Corporate
    legal_form: LegalFormEnum.optional().nullable(),
    rc_number: z.string().optional(),
    rc_city: z.string().optional(),
    if_number: z.string().optional(),
    activity_sector: z.string().optional(),
    legal_representative: z.string().optional(),
    representative_role: z.string().optional(),
    company_creation_date: z.string().optional(),

    // Notes
    notes: z.string().optional(),
  }).superRefine(async (data, ctx) => {
    // Unique Name Check
    if (!(isEdit && currentRow.name === data.name)) {
      if (!await check_username(data.name)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('client_name_taken'),
          path: ['name']
        })
      }
    }

    // ICE Validation (Corporate only)
    if (data.client_type === 'corporate') {
      if (data.idnumber && data.idnumber.length > 0) {
        // Must be exactly 15 digits
        if (!/^\d{15}$/.test(data.idnumber)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('ice_invalid'),
            path: ['idnumber']
          });
        }
      }
    }
  });

  type ClientForm = z.infer<typeof formSchema>

  const form = useForm<ClientForm>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      client_type: 'individual',
      name: '',
      email: '',
      phone: '',
      idnumber: '',
      // Initialize other fields empty
    },
  })

  // Watch client type to conditionally render fields
  const clientType = form.watch('client_type');

  useEffect(() => {
    if (isEdit && currentRow) {
      form.reset({
        ...currentRow,
        email: currentRow.email || '',
        phone: currentRow.phone || '',
        address: currentRow.address || undefined,
        city: currentRow.city || undefined,
        country: currentRow.country || undefined,
        notes: currentRow.notes || undefined,
        // Ensure client_type matches schema enum or defaults
        client_type: currentRow.client_type || 'individual',
        // Handle potential nulls for other fields explicitly
        idnumber: currentRow.idnumber || undefined,
        id_type: currentRow.id_type || undefined,
        birth_date: currentRow.birth_date || undefined,
        nationality: currentRow.nationality || undefined,
        profession: currentRow.profession || undefined,
        legal_form: currentRow.legal_form || undefined,
        rc_number: currentRow.rc_number || undefined,
        rc_city: currentRow.rc_city || undefined,
        if_number: currentRow.if_number || undefined,
        activity_sector: currentRow.activity_sector || undefined,
        legal_representative: currentRow.legal_representative || undefined,
        representative_role: currentRow.representative_role || undefined,
        company_creation_date: currentRow.company_creation_date || undefined,
      });
    } else {
      form.reset({
        client_type: 'individual',
        name: '',
        email: '',
        phone: '',
      })
    }
  }, [currentRow, isEdit, form]);

  const onSubmit = (values: ClientForm) => {
    // Filter out empty strings for better data cleanliness before sending
    const cleanValues = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, v === '' ? null : v])
    );

    const req = {
      ...cleanValues
    } as any; // Allow any for API flexibility, strict type checks handled by Schema

    const future = isEdit
      ? update_client(currentRow?.id, req)
      : add_client(req)

    future
      .then((apiResponse) => {
        form.reset()
        toast({
          title: t(isEdit ? 'client_updated' : 'client_added', { client: values.name }),
        })
        onOpenChange(false)
        if (isEdit && onUpdateSuccess) {
          onUpdateSuccess(apiResponse as Client);
        } else {
          if (onUpdateSuccess) onUpdateSuccess(apiResponse as Client);
          else setParams({ ...params });
        }
      })
      .catch(e => {
        toast({
          variant: 'destructive',
          title: e?.response?.data?.error != null ? e.response.data.error : t('serv_error')
        })
      });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(state) => {
        form.reset()
        onOpenChange(state)
      }}
    >
      <DialogContent className='sm:max-w-xl max-h-[90vh] overflow-y-auto' dir={useTranslation().i18n.dir()}>
        <DialogHeader className='text-start'>
          <DialogTitle>{isEdit ? t('edit_client') : t('add_client')}</DialogTitle>
          <DialogDescription className="text-start">
            {t('user_action')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form id='client-form' onSubmit={form.handleSubmit(onSubmit, (errors) => {
              const messages = Object.values(errors)
                .map((e: any) => e?.message)
                .filter(Boolean) as string[]
              toast({
                title: t('validation_failed'),
                description: messages.join(' · '),
                variant: 'destructive',
              })
            })} className='space-y-6'>

            {/* A client's type is fixed after creation so its type-specific data stays consistent. */}
            {!isEdit && (
              <FormField
                control={form.control}
                name="client_type"
                render={({ field }) => (
                  <Tabs value={field.value} onValueChange={field.onChange} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="individual">{t('individual')}</TabsTrigger>
                      <TabsTrigger value="corporate">{t('corporate')}</TabsTrigger>
                    </TabsList>
                  </Tabs>
                )}
              />
            )}

            <div className="space-y-4">
              {/* 2. General Information Section */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">{t('general_info')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name='name'
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>{t('client_name')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('name_placeholder')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name='email'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('email')}</FormLabel>
                        <FormControl>
                          <Input placeholder='client@example.com' {...field} value={field.value || ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name='phone'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('phone')}</FormLabel>
                        <FormControl>
                          <PhoneInput {...field} value={field.value || ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name='address'
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>{t('address')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('address_placeholder')} {...field} value={field.value || ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name='city'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('city')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('city')} {...field} value={field.value || ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name='country'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('country')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('country')} {...field} value={field.value || ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              {/* 3. Specific Information Section */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">{t('specific_info')}</h3>

                {clientType === 'individual' ? (
                  /* INDIVIDUAL FIELDS */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name='id_type'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('id_type')}</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value || undefined}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder={t('select')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="cin">{t('cin')}</SelectItem>
                              <SelectItem value="passport">{t('passport')}</SelectItem>
                              <SelectItem value="residence_card">{t('residence_card')}</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='idnumber'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('id_number')}</FormLabel>
                          <FormControl>
                            <Input placeholder={t('idnumber_placeholder')} {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='birth_date'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('birth_date')}</FormLabel>
                          <FormControl>
                            <DatePicker
                              date={field.value ? new Date(field.value) : undefined}
                              setDate={(date) => field.onChange(date ? date.toISOString().split('T')[0] : '')}
                              placeholder={t('pick_a_date')}
                              maxDate={new Date()}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='nationality'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('nationality')}</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='profession'
                      render={({ field }) => (
                        <FormItem className="col-span-2">
                          <FormLabel>{t('profession')}</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                ) : (
                  /* CORPORATE FIELDS */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name='legal_form'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('legal_form')}</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value || undefined}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder={t('select')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="sarl">SARL</SelectItem>
                              <SelectItem value="sa">SA</SelectItem>
                              <SelectItem value="sas">SAS</SelectItem>
                              <SelectItem value="snc">SNC</SelectItem>
                              <SelectItem value="auto">Auto-Entrepreneur</SelectItem>
                              <SelectItem value="other">{t('other')}</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='idnumber'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ICE</FormLabel>
                          <FormControl>
                            <Input placeholder={t('ice_placeholder')} {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='rc_number'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('rc_number')}</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='rc_city'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('rc_city')}</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='if_number'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('if_number')}</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='activity_sector'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('activity_sector')}</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='legal_representative'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('legal_representative')}</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='representative_role'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('representative_role')}</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>

              <Separator />

              {/* 4. Notes Section */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">{t('internal_notes')}</h3>
                <FormField
                  control={form.control}
                  name='notes'
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea placeholder={t('notes')} className="min-h-[100px]" {...field} value={field.value || ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

            </div>

          </form>
        </Form>
        <DialogFooter>
          <Button type='submit' form='client-form'>
            {t('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
